import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Phone, Loader2, Home } from 'lucide-react'
import { toast } from 'sonner'
import { filterContacts } from './contact-search'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { contactsApiClient } from '@/lib/api/contacts.client'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { livekitClient } from '@/lib/api/livekit.client'
import {
  type ContactWithRelationships,
  type Listing,
  formatUSPhoneForDisplay,
  formatVariableLabel,
} from '@super/shared-types'
import { AVAILABLE_VARIABLES, type Variable } from '@/features/agent-detail'

interface SimulateCallModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agentId: string
  companyId: string
  companyPhone: string
  agentVariables?: string[]
  triggerType?: string
}

// Map agent triggerType → Python-side OutboundCallType so the dispatcher
// builds the correct agent class (LeaseAgent / DelinquencyAgent / …).
function callTypeFromTrigger(
  triggerType: string | undefined,
): 'LEASING' | 'DELINQUENCY' | undefined {
  switch (triggerType) {
    case 'PAYMENT_OVERDUE_WEBHOOK':
      return 'DELINQUENCY'
    case 'LEAD_WEBHOOK':
      return 'LEASING'
    default:
      return undefined
  }
}

// System variables resolved automatically by the backend — not shown to user
const SYSTEM_VARIABLES = new Set(['agentName', 'companyName'])

// Variables that can auto-fill from the selected contact
const CONTACT_AUTO_FILL: Record<
  string,
  (c: ContactWithRelationships) => string | undefined
> = {
  firstName: c => c.firstName || undefined,
  lastName: c => c.lastName || undefined,
  fullName: c =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || undefined,
  contactName: c =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || undefined,
  phone: c => c.primaryPhoneNumber || undefined,
  email: c => c.primaryEmail || undefined,
}

// Build a lookup of variable metadata from the canonical variables list
const VARIABLE_META: Record<string, Variable> = Object.fromEntries(
  AVAILABLE_VARIABLES.map(v => [v.name, v]),
)

type ContactItem = SearchDropdownItem<{
  contactId?: string
  phone: string
  isManualEntry?: boolean
}>

type ListingItem = SearchDropdownItem<{ listing: Listing }>

function contactToItem(contact: ContactWithRelationships): ContactItem {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  const phone = contact.primaryPhoneNumber || ''
  const displayPhone = phone ? formatUSPhoneForDisplay(phone) : ''
  const email = contact.primaryEmail || ''

  const sublabelParts = [displayPhone, email].filter(Boolean)

  return {
    id: contact.id,
    label: name || displayPhone || 'Unknown',
    sublabel: sublabelParts.join(' · '),
    icon: <Phone className="h-4 w-4 text-muted-foreground" />,
    data: {
      contactId: contact.id,
      phone,
    },
  }
}

// --- Main modal component ---

export function SimulateCallModal({
  open,
  onOpenChange,
  agentId,
  companyId,
  companyPhone,
  agentVariables,
  triggerType,
}: SimulateCallModalProps) {
  const [selectedContact, setSelectedContact] = useState<ContactItem | null>(
    null,
  )
  const [selectedListing, setSelectedListing] = useState<ListingItem | null>(
    null,
  )
  const [allListingItems, setAllListingItems] = useState<ListingItem[]>([])
  const [isInitiating, setIsInitiating] = useState(false)

  // Show only agent-specific variables (excluding system-resolved ones).
  // If agentVariables is provided, filter to only those; otherwise fall back to all.
  const variableNames = useMemo(() => {
    if (!agentVariables?.length) {
      return AVAILABLE_VARIABLES.filter(v => !SYSTEM_VARIABLES.has(v.name)).map(
        v => v.name,
      )
    }

    // Use agent's variables, preserving order, excluding system ones
    return agentVariables.filter(name => !SYSTEM_VARIABLES.has(name))
  }, [agentVariables])
  const [variables, setVariables] = useState<Record<string, string>>({})

  // Auto-fill variables when a contact is selected
  const selectedContactRef = useRef<ContactWithRelationships | null>(null)
  const contactsCache = useRef<ContactWithRelationships[]>([])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedContact(null)
      setSelectedListing(null)
      setVariables({})
      setIsInitiating(false)
      selectedContactRef.current = null
    }
  }, [open])

  const updateVariablesFromContact = useCallback(
    (contact: ContactWithRelationships | null) => {
      if (!contact) return
      const autoFilled: Record<string, string> = {}
      for (const varName of variableNames) {
        const filler = CONTACT_AUTO_FILL[varName]
        if (filler) {
          const value = filler(contact)
          if (value) autoFilled[varName] = value
        }
      }
      setVariables(prev => ({ ...prev, ...autoFilled }))
    },
    [variableNames],
  )

  // Fetch all contacts once when modal opens, then filter client-side
  const [allContactItems, setAllContactItems] = useState<ContactItem[]>([])

  useEffect(() => {
    if (!open) return
    contactsApiClient
      .getAllAdminContactsForCompany(companyId)
      .then(contacts => {
        contactsCache.current = contacts
        setAllContactItems(
          contacts.filter(c => c.primaryPhoneNumber).map(contactToItem),
        )
      })
  }, [open, companyId])

  useEffect(() => {
    if (!open) return
    knowledgeApiClient.getListings().then((listings: Listing[]) => {
      setAllListingItems(
        listings.map((l: Listing) => ({
          id: l.id,
          label: l.address,
          sublabel: [
            l.unit ? `Unit ${l.unit}` : null,
            l.beds != null ? `${l.beds}bd` : null,
            l.baths != null ? `${l.baths}ba` : null,
            l.monthlyRentDollars ? `$${l.monthlyRentDollars}/mo` : null,
          ]
            .filter(Boolean)
            .join(' · '),
          icon: <Home className="h-4 w-4 text-muted-foreground" />,
          data: { listing: l },
        })),
      )
    })
  }, [open])

  const fetchContacts = useCallback(
    async (query?: string): Promise<ContactItem[]> =>
      filterContacts(allContactItems, query, (e164, raw) => ({
        id: `manual-${e164}`,
        label: `Call ${formatUSPhoneForDisplay(e164) || raw}`,
        sublabel: 'Manual entry',
        icon: <Phone className="h-4 w-4 text-muted-foreground" />,
        data: { phone: e164, isManualEntry: true },
      })),
    [allContactItems],
  )

  const fetchListings = useCallback(
    async (query?: string): Promise<ListingItem[]> => {
      if (!query?.trim()) return allListingItems
      const q = query.toLowerCase()
      return allListingItems.filter(
        item =>
          item.label.toLowerCase().includes(q) ||
          item.sublabel?.toLowerCase().includes(q),
      )
    },
    [allListingItems],
  )

  const emptyVars = variableNames.filter(v => !variables[v]?.trim())

  const handleInitiateCall = async () => {
    if (!selectedContact?.data?.phone) return

    setIsInitiating(true)
    try {
      const roomName = `outbound-simulation-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`
      const callType = callTypeFromTrigger(triggerType)

      // 2. Dispatch the Python agent into the LiveKit room
      await livekitClient.dispatchAgent({
        agentName: 'outbound-caller',
        roomName,
        metadata: {
          phoneTree: [variables.phone?.trim() || selectedContact.data.phone],
          from: companyPhone,
          companyId,
          contactId: selectedContact.data.contactId,
          agentId,
          ...(callType && { callType }),
          // Top-level name fields so Python agent can pass them to conference creation
          // (avoids "Unknown contact" in post-call summaries)
          ...(variables.firstName && { firstName: variables.firstName }),
          ...(variables.lastName && { lastName: variables.lastName }),
          ...(variables.contactName && {
            customerName: variables.contactName,
          }),
          // property is the listing address string — same field the lead creation flow sends
          ...(selectedListing?.data && {
            property: selectedListing.data.listing.address,
          }),
          // Delinquency agent reads these top-level (not from variableValues)
          ...(callType === 'DELINQUENCY' && {
            rentAmount: variables.amountOwed,
            rentDueDate: variables.dueDate,
            overdueAmount: variables.amountOwed,
            daysOverdue: parseInt(variables.daysOverdue ?? '0', 10) || 0,
          }),
          variableValues: variables,
        },
      })

      toast.success('Outbound call initiated')
      onOpenChange(false)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to initiate call'
      toast.error(message)
    } finally {
      setIsInitiating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Simulate outbound call</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Enter or select contact
            </label>
            <SearchDropdown<ContactItem['data']>
              fetchItems={fetchContacts}
              loadInitialItems={true}
              selectedItems={selectedContact}
              onSelect={item => {
                const selected = item as ContactItem | null
                setSelectedContact(selected)
                if (selected?.data?.contactId) {
                  const contact = contactsCache.current.find(
                    c => c.id === selected.data?.contactId,
                  )
                  selectedContactRef.current = contact || null
                  updateVariablesFromContact(contact || null)
                } else {
                  selectedContactRef.current = null
                }
              }}
              placeholder="Chris Marlin or 555-555-5555"
              searchPlaceholder="Search by name, phone, or email..."
              emptyMessage="No contacts found"
              maxHeight="320px"
              debounceMs={100}
            />
          </div>

          {allListingItems.length > 0 && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Listing (optional)
              </label>
              <SearchDropdown<ListingItem['data']>
                fetchItems={fetchListings}
                loadInitialItems={true}
                selectedItems={selectedListing}
                onSelect={item =>
                  setSelectedListing(item as ListingItem | null)
                }
                placeholder="Search listings..."
                searchPlaceholder="Search by address..."
                emptyMessage="No listings found"
                maxHeight="320px"
                debounceMs={100}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Select a listing so the agent knows which property this call is
                about from the start.
              </p>
            </div>
          )}

          {variableNames.length > 0 && (
            <div>
              <label className="text-sm font-medium mb-3 block">
                Variables
              </label>
              <div className="space-y-3">
                {variableNames.map(varName => {
                  const meta = VARIABLE_META[varName]
                  const placeholder =
                    meta?.example ||
                    (CONTACT_AUTO_FILL[varName]
                      ? 'Auto-filled from contact'
                      : '')
                  return (
                    <div key={varName} className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-muted-foreground w-32 shrink-0">
                          {formatVariableLabel(varName)}
                        </label>
                        <Input
                          placeholder={placeholder}
                          value={variables[varName] || ''}
                          onChange={e =>
                            setVariables(prev => ({
                              ...prev,
                              [varName]: e.target.value,
                            }))
                          }
                        />
                      </div>
                      {meta?.description && (
                        <p className="text-xs text-muted-foreground ml-[calc(8rem+0.75rem)]">
                          {meta.description}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {emptyVars.length > 0 && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Missing: {emptyVars.map(formatVariableLabel).join(', ')}. The
              agent may not have full context.
            </p>
          )}

          <Button
            onClick={handleInitiateCall}
            disabled={!selectedContact?.data?.phone || isInitiating}
            className="w-full"
          >
            {isInitiating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Initiating...
              </>
            ) : (
              <>
                <Phone className="h-4 w-4 mr-2" />
                Initiate call
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
